/* eslint-disable */
import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import MemberAvatar from "../components/MemberAvatar";

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function AdminModeration() {
  const [tab, setTab] = useState("summary");
  const [summary, setSummary] = useState(null);
  const [audit, setAudit] = useState([]);
  const [deleted, setDeleted] = useState([]);
  const [hidden, setHidden] = useState([]);
  const [suspended, setSuspended] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, a, d, h, u] = await Promise.all([
        api.get("/admin/moderation/summary"),
        api.get("/admin/moderation-log", { params: { limit: 200 } }),
        api.get("/admin/posts/deleted", { params: { limit: 200 } }),
        api.get("/admin/posts/hidden", { params: { limit: 200 } }),
        api.get("/admin/users/suspended"),
      ]);
      setSummary(s.data);
      setAudit(a.data?.log || []);
      setDeleted(d.data?.deleted_posts || []);
      setHidden(h.data?.hidden_posts || []);
      setSuspended(u.data?.suspended_users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load moderation data");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const restorePost = async (pid) => {
    const reason = window.prompt("Restore reason (optional):", "");
    try {
      await api.post(`/admin/posts/${pid}/restore`, null, { params: reason ? { reason } : {} });
      toast.success("Post restored");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    }
  };

  const unhidePost = async (pid) => {
    try {
      await api.post(`/admin/posts/${pid}/unhide`, { reason: null });
      toast.success("Post revealed");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unhide failed");
    }
  };

  const suspendUser = async (u) => {
    const reason = window.prompt(`Reason for suspending ${u.email}?`, "");
    if (reason === null) return;
    try {
      await api.post(`/admin/users/${u.user_id}/suspend`, { reason });
      toast.success("User suspended · sessions revoked");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suspend failed");
    }
  };

  const unsuspend = async (u) => {
    if (!window.confirm(`Reinstate ${u.email}?`)) return;
    try {
      await api.post(`/admin/users/${u.user_id}/unsuspend`, { reason: null });
      toast.success("Account reinstated");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unsuspend failed");
    }
  };

  const [suspendEmail, setSuspendEmail] = useState("");
  const suspendByEmail = async () => {
    const email = suspendEmail.trim().toLowerCase();
    if (!email) { toast.error("Enter an email"); return; }
    try {
      // Find user_id via /api/admin/members-lookup? — fall back to /api/members
      const r = await api.get("/members");
      const match = (r.data || []).find(x => (x.email || "").toLowerCase() === email);
      if (!match) { toast.error("No member found with that email"); return; }
      const reason = window.prompt(`Reason for suspending ${email}?`, "");
      if (reason === null) return;
      await api.post(`/admin/users/${match.user_id}/suspend`, { reason });
      setSuspendEmail("");
      toast.success("Suspended");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not suspend");
    }
  };

  const downloadCsv = async () => {
    try {
      const r = await api.get("/admin/moderation-log/csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `metier-moderation-log-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("CSV export failed");
    }
  };

  return (
    <div className="m-admin-section" data-testid="admin-moderation">
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <div>
          <span className="m-eyebrow">Admin · Community moderation</span>
          <h2 className="m-h2" style={{ margin: "4px 0 0" }}>Keep the feed clean.</h2>
          <p className="m-muted small" style={{ marginTop: 6 }}>
            Hide or delete posts, suspend abusive members, and audit every action.
          </p>
        </div>
        <button className="m-btn-ghost" onClick={downloadCsv} data-testid="admin-mod-csv-btn">
          Export audit log · CSV ↓
        </button>
      </div>

      {summary && (
        <div className="m-stats-grid" style={{ marginBottom: 20 }}>
          <div className="m-stat" data-testid="admin-mod-stat-deleted">
            <span className="m-stat-num">{summary.deleted_posts}</span>
            <span className="m-stat-label">Deleted posts</span>
          </div>
          <div className="m-stat" data-testid="admin-mod-stat-hidden">
            <span className="m-stat-num">{summary.hidden_posts}</span>
            <span className="m-stat-label">Hidden posts</span>
          </div>
          <div className="m-stat" data-testid="admin-mod-stat-suspended">
            <span className="m-stat-num">{summary.suspended_users}</span>
            <span className="m-stat-label">Suspended</span>
          </div>
          <div className="m-stat" data-testid="admin-mod-stat-audit">
            <span className="m-stat-num">{summary.audit_log_entries}</span>
            <span className="m-stat-label">Audit entries</span>
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, borderBottom: "1px solid rgba(27,35,64,0.12)", marginBottom: 20, flexWrap: "wrap" }}>
        {[
          ["summary", "Recent actions"],
          ["deleted", `Deleted (${deleted.length})`],
          ["hidden", `Hidden (${hidden.length})`],
          ["suspended", `Suspended (${suspended.length})`],
        ].map(([k, label]) => (
          <button key={k} type="button"
            onClick={() => setTab(k)}
            data-testid={`admin-mod-tab-${k}`}
            style={{
              background: "none", border: "none", padding: "10px 14px", cursor: "pointer",
              fontWeight: tab === k ? 700 : 500,
              borderBottom: tab === k ? "2px solid var(--gold)" : "2px solid transparent",
              color: tab === k ? "var(--m-text)" : "rgba(27,35,64,0.6)",
              letterSpacing: 0.12, fontSize: 13,
            }}>
            {label}
          </button>
        ))}
      </div>

      {loading && <div className="m-loading-inline">Loading moderation data…</div>}

      {!loading && tab === "summary" && (
        <div data-testid="admin-mod-audit">
          <h3 className="m-side-title">Audit log · latest 200</h3>
          {audit.length === 0 ? (
            <div className="m-empty small">No moderation actions yet.</div>
          ) : (
            <div className="m-card" style={{ padding: 0, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(27,35,64,0.12)", textAlign: "left" }}>
                    <th style={{ padding: "10px 12px" }}>When</th>
                    <th style={{ padding: "10px 12px" }}>Action</th>
                    <th style={{ padding: "10px 12px" }}>Target</th>
                    <th style={{ padding: "10px 12px" }}>Actor</th>
                    <th style={{ padding: "10px 12px" }}>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.map((row) => (
                    <tr key={row.log_id} style={{ borderBottom: "1px solid rgba(27,35,64,0.05)" }}>
                      <td style={{ padding: "8px 12px", fontVariantNumeric: "tabular-nums" }}>{fmtDate(row.at)}</td>
                      <td style={{ padding: "8px 12px", fontWeight: 600, textTransform: "uppercase", fontSize: 10.5, letterSpacing: 0.14 }}>
                        {row.action}
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        <div style={{ fontSize: 11, textTransform: "uppercase", opacity: 0.6 }}>{row.target_type}</div>
                        <code style={{ fontSize: 11 }}>{row.target_id}</code>
                      </td>
                      <td style={{ padding: "8px 12px" }}>{row.actor_email}</td>
                      <td style={{ padding: "8px 12px", color: "rgba(27,35,64,0.75)" }}>{row.reason || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {!loading && tab === "deleted" && (
        <div data-testid="admin-mod-deleted">
          {deleted.length === 0 ? (
            <div className="m-empty small">No soft-deleted posts.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {deleted.map((p) => (
                <div key={p.post_id} className="m-card" data-testid={`admin-mod-deleted-${p.post_id}`}
                     style={{ padding: 14, display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>@{p.user_id}</div>
                    <div style={{ fontStyle: "italic", opacity: 0.75, whiteSpace: "pre-wrap" }}>"{p.body}"</div>
                    <div className="m-muted small" style={{ marginTop: 8 }}>
                      Deleted {fmtDate(p.deleted_at)}
                      {p.deleted_by_admin && " · by admin"}
                      {p.moderation_reason && ` · "${p.moderation_reason}"`}
                    </div>
                  </div>
                  <button className="m-btn-gold" style={{ padding: "6px 14px", fontSize: 12 }}
                    onClick={() => restorePost(p.post_id)}
                    data-testid={`admin-mod-restore-${p.post_id}`}>Restore</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!loading && tab === "hidden" && (
        <div data-testid="admin-mod-hidden">
          {hidden.length === 0 ? (
            <div className="m-empty small">No hidden posts. Use the "Hide" action from any post's admin menu when you need to quietly pull something from the feed.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {hidden.map((p) => (
                <div key={p.post_id} className="m-card" data-testid={`admin-mod-hidden-${p.post_id}`}
                     style={{ padding: 14, display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start" }}>
                  <div style={{ flex: 1, display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <MemberAvatar user={{ user_id: p.user_id, name: p.user_name || p.user_id, picture: p.user_picture, initials: p.user_initials }} size={40} clickable={false} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>{p.user_name || `@${p.user_id}`}</div>
                      <div style={{ fontStyle: "italic", opacity: 0.75, whiteSpace: "pre-wrap" }}>"{p.body}"</div>
                      <div className="m-muted small" style={{ marginTop: 8 }}>
                        Hidden {fmtDate(p.hidden_at)}
                        {p.hidden_reason && ` · "${p.hidden_reason}"`}
                      </div>
                    </div>
                  </div>
                  <button className="m-btn-gold" style={{ padding: "6px 14px", fontSize: 12 }}
                    onClick={() => unhidePost(p.post_id)}
                    data-testid={`admin-mod-unhide-${p.post_id}`}>Reveal</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!loading && tab === "suspended" && (
        <div data-testid="admin-mod-suspended">
          <div className="m-card" style={{ padding: 16, marginBottom: 12, background: "rgba(184,145,58,0.06)" }}>
            <h4 className="rec-h3" style={{ margin: "0 0 8px" }}>Suspend by email</h4>
            <div style={{ display: "flex", gap: 8 }}>
              <input value={suspendEmail} onChange={e => setSuspendEmail(e.target.value)}
                placeholder="member@example.com"
                data-testid="admin-mod-suspend-email"
                style={{ flex: 1, padding: "8px 12px", border: "1px solid var(--m-border)", borderRadius: 9, background: "var(--cream)" }} />
              <button className="m-btn-gold" onClick={suspendByEmail} data-testid="admin-mod-suspend-btn">
                Suspend
              </button>
            </div>
            <p className="m-muted small" style={{ marginTop: 8 }}>
              Suspending revokes all active sessions and blocks login + post creation. Admin accounts can't be suspended.
            </p>
          </div>

          {suspended.length === 0 ? (
            <div className="m-empty small">No suspended accounts.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {suspended.map((u) => (
                <div key={u.user_id} className="m-card" data-testid={`admin-mod-suspended-${u.user_id}`}
                     style={{ padding: 14, display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start" }}>
                  <div style={{ flex: 1, display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <MemberAvatar user={u} size={40} clickable={false} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600 }}>{u.name || u.email}</div>
                      <div className="m-muted small">{u.email} · {u.city || "—"}</div>
                      <div className="m-muted small" style={{ marginTop: 6 }}>
                        Suspended {fmtDate(u.suspended_at)}
                        {u.suspended_reason && ` · "${u.suspended_reason}"`}
                      </div>
                    </div>
                  </div>
                  <button className="m-btn-ghost" style={{ padding: "6px 14px", fontSize: 12 }}
                    onClick={() => unsuspend(u)}
                    data-testid={`admin-mod-unsuspend-${u.user_id}`}>Reinstate</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
